import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  FileText,
  ClipboardCheck,
  FileCheck,
  Users,
  ScrollText,
  LogOut,
  Menu,
  X,
  Settings,
  BarChart3,
  DollarSign,
  Lock,
  Trash2,
  ShieldCheck,
  ChevronDown,
  Briefcase,
  Landmark,
  UserCog,
  Bell
} from 'lucide-react';

// Use environment variable for logo URL with fallback
const LOGO_URL = process.env.REACT_APP_LOGO_URL || '/sobs-logo.png';

const NAV_GROUPS = [
  {
    key: 'overview', label: 'Overview', icon: LayoutDashboard,
    items: [
      { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['super_admin', 'special_admin', 'admin', 'user', 'special_inspection', 'v_inspector'] },
      { to: '/admin/reports', icon: BarChart3, label: 'Reports', roles: ['super_admin', 'special_admin', 'admin', 'user', 'special_inspection', 'v_inspector'] },
    ],
  },
  {
    key: 'operations', label: 'Cargo Operations', icon: Briefcase,
    items: [
      { to: '/admin/manifests', icon: FileText, label: 'Manifests', roles: ['super_admin', 'special_admin', 'admin', 'user', 'special_inspection', 'manifest_officer', 'v_inspector'] },
      { to: '/admin/inspections', icon: ClipboardCheck, label: 'Inspections', roles: ['super_admin', 'special_admin', 'admin', 'user', 'special_inspection', 'v_inspector'] },
      { to: '/admin/review', icon: ShieldCheck, label: 'Review', roles: ['super_admin', 'special_admin', 'admin'] },
      { to: '/admin/gate-passes', icon: FileCheck, label: 'Gate Passes', roles: ['super_admin', 'special_admin', 'admin', 'user', 'special_inspection', 'v_inspector'] },
      { to: '/admin/gate-point-passes', icon: FileCheck, label: 'Gate Passes', roles: ['gate_point'] },  // Gate Point users see limited view
    ],
  },
  {
    key: 'finance', label: 'Finance', icon: Landmark,
    items: [
      { to: '/admin/finance', icon: DollarSign, label: 'Finance', roles: ['super_admin', 'special_admin', 'admin', 'finance', 'special_finance'] },
      { to: '/admin/finance-audit', icon: ScrollText, label: 'Finance Audit', roles: ['super_admin', 'special_admin', 'admin', 'special_finance'] },
    ],
  },
  {
    key: 'administration', label: 'Administration', icon: UserCog,
    items: [
      { to: '/admin/users', icon: Users, label: 'User Management', roles: ['super_admin', 'special_admin'] },
      { to: '/admin/audit-logs', icon: ScrollText, label: 'Audit Logs', roles: ['super_admin', 'special_admin'] },
      { to: '/admin/trash', icon: Trash2, label: 'Trash Bin', roles: ['super_admin', 'special_admin'] },
      { to: '/admin/security', icon: Lock, label: 'Security', roles: ['super_admin', 'special_admin', 'admin', 'user', 'special_inspection', 'finance', 'special_finance', 'gate_point', 'manifest_officer', 'v_inspector'] },
      { to: '/admin/security-settings', icon: Settings, label: 'Security Settings', roles: ['super_admin'] },
    ],
  },
];

const initials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || 'U';

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState({});

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const groups = NAV_GROUPS
    .map(g => ({ ...g, items: g.items.filter(i => i.roles.includes(user?.role)) }))
    .filter(g => g.items.length > 0);

  const toggle = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-[#0B2545]/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - IMIS Staff Portal style */}
      <aside
        data-testid="sidebar"
        className={`sidebar fixed left-0 top-0 h-full w-64 z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? 'open' : ''
        }`}
      >
        {/* Brand */}
        <div className="sidebar-logo-section px-4 py-4 flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <img src={LOGO_URL} alt="SoBS Logo" className="h-8 w-auto object-contain shrink-0" />
              <div className="h-8 w-px bg-slate-200 shrink-0" />
              <div className="leading-tight min-w-0">
                <p className="text-[14px] font-extrabold text-[#0B2545] tracking-tight whitespace-nowrap">Staff Portal</p>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#B8860B] whitespace-nowrap">SOBS-AAIA</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-2 hover:bg-[#F7F5F0] rounded-sm transition-colors shrink-0"
              data-testid="sidebar-close-btn"
            >
              <X className="h-5 w-5 text-[#0B2545]" />
            </button>
            <span className="hidden xl:flex h-8 w-8 items-center justify-center text-[#0B2545]/70 shrink-0" aria-hidden="true">
              <Bell className="h-4 w-4" />
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 px-3 overflow-y-auto space-y-1">
          {groups.map((group) => {
            const isGroupActive = group.items.some(i => location.pathname.startsWith(i.to));
            const open = !collapsed[group.key];
            return (
              <div key={group.key} data-testid={`nav-group-${group.key}`}>
                <button
                  type="button"
                  onClick={() => toggle(group.key)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-left transition-colors ${
                    isGroupActive ? 'bg-[#F4F6F9]' : 'hover:bg-[#F7F5F0]'
                  }`}
                  data-testid={`nav-group-toggle-${group.key}`}
                >
                  <span className="h-9 w-9 flex items-center justify-center bg-[#FFC107] text-[#0B2545] rounded-sm shrink-0">
                    <group.icon className="h-4 w-4" />
                  </span>
                  <span className="flex-1 text-[15px] font-semibold text-[#0B2545]">{group.label}</span>
                  <ChevronDown className={`h-4 w-4 text-[#0B2545]/60 transition-transform ${open ? '' : '-rotate-90'}`} />
                </button>

                {open && (
                  <div className="ml-5 mt-1 pl-3 border-l-2 border-[#FFC107]/70 space-y-0.5">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={onClose}
                        className={({ isActive }) =>
                          `sidebar-menu-item group flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors duration-200 ${
                            isActive
                              ? 'active bg-[#0B2545] text-white font-bold'
                              : 'text-[#0B2545]/80 font-medium hover:bg-[#F7F5F0] hover:text-[#0B2545]'
                          }`
                        }
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-[#0B2545]/60 group-hover:text-[#0B2545]'}`} />
                            <span className="flex-1">{item.label}</span>
                            {isActive && <span className="h-2 w-2 rounded-full bg-[#FFC107]" />}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Signed in as */}
        <div className="sidebar-profile p-4 flex-shrink-0 bg-[#F4F6F9]">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#0B2545]/60 mb-3">Signed in as</p>
          <div className="flex items-center gap-3 mb-4" data-testid="sidebar-user-card">
            <div className="h-11 w-11 rounded-full bg-[#FFC107] text-[#0B2545] flex items-center justify-center font-extrabold text-sm shrink-0">
              {initials(user?.full_name)}
            </div>
            <div className="min-w-0">
              <p className="text-[15px] font-bold text-[#0B2545] truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-500 capitalize truncate">{user?.role?.replace(/_/g, ' ')}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center justify-between w-full px-5 py-3 text-base font-bold text-[#0B2545] bg-[#FFC107] hover:bg-[#E6AD06] rounded-sm transition-colors duration-200 group"
            data-testid="logout-btn"
          >
            <span>Logout</span>
            <LogOut className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </aside>
    </>
  );
};

export const MobileMenuButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="lg:hidden p-2.5 hover:bg-[#F7F5F0] rounded-sm transition-colors border border-slate-200"
    data-testid="mobile-menu-btn"
  >
    <Menu className="h-5 w-5 text-[#0B2545]" />
  </button>
);

export default Sidebar;
